import React, { useCallback, useState } from 'react';
import { useQuery, useQueryCache } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { UsersAPI } from 'api/UsersAPI';
import { Layout, PageHeader, Space } from 'antd';
import Column from 'antd/lib/table/Column';
import { ProfileOwner, ProfileOwnerDTO } from 'models/user';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { Content } from 'common/StyledComponents';
import { UserStatus } from 'models/userStatus';
import { DeleteProfileOwnerButton } from 'pages/SuperAdminModule/ProfileOwnersPage/DeleteProfileOwnerButton';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { toSimpleDate } from 'common/dateFnsUtils';
import { CloseOutlined } from '@ant-design/icons/lib';

export const ProfileOwnersPage = () => {
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);

  const queryCache = useQueryCache();
  const { data, isFetching } = useQuery(
    [QueryKey.FamilyUsers, pageMetaData],
    () => UsersAPI.fetchProfileOwners(pageMetaData),
    {
      keepPreviousData: true,
    }
  );

  const invalidateProfileOwners = useCallback(
    () => queryCache.invalidateQueries(QueryKey.FamilyUsers),
    [queryCache]
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const intlMessage = useFormatMessage();

  return (
    <Layout>
      <Content>
        <PageHeader title={intlMessage({ id: 'users' })} />
        <NumberedTable
          rowKey="id"
          loading={isFetching}
          dataSource={data?.content}
          pagination={paginationConfig}
          scroll={{ x: 'auto' }}
        >
          <Column
            title={intlMessage({ id: 'name' })}
            dataIndex="firstName"
            render={
              (value, record: ProfileOwnerDTO) =>
                // <Link
                //   key={`${url}/${record.userId}`}
                //   to={`${url}/${record.userId}`}
                // >
                `${record.firstName} ${record.lastName}`
              // </Link>
            }
          />
          <Column title="Email/Login" dataIndex="email" />
          <Column
            title={intlMessage({ id: 'create_date' })}
            dataIndex="createDate"
            render={(value) => toSimpleDate(value)}
            width={150}
          />
          <Column
            title={intlMessage({ id: 'familyUsers.lastLogin' })}
            dataIndex="lastLoginDate"
            render={(value) => (value === null ? '--' : toSimpleDate(value))}
            align="center"
            width={150}
          />
          <Column
            title={intlMessage({ id: 'partner' })}
            dataIndex={['partnerDetails', 'name']}
            align="center"
            render={(value) => value || <CloseOutlined />}
          />
          <Column
            title={intlMessage({ id: 'status' })}
            dataIndex="status"
            render={(value) => <UserStatusTag status={value} />}
          />
          <Column
            title={intlMessage({ id: 'device' })}
            key="device"
            align="right"
            render={(value, record: ProfileOwner) =>
              record.assignedDevice
                ? record.assignedDevice?.serialNumber
                : 'n/a'
            }
          />

          <Column
            title={intlMessage({ id: 'actions' })}
            key="actions"
            align="right"
            render={(value, record: ProfileOwner) => (
              <Space direction={'vertical'} style={{ width: '150px' }}>
                {record.status !== UserStatus.Archived && (
                  <DeleteProfileOwnerButton
                    danger
                    profileOwner={record}
                    onDeleted={invalidateProfileOwners}
                  />
                )}
              </Space>
            )}
          />
        </NumberedTable>
      </Content>
    </Layout>
  );
};
